import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';

/**
 * Custom Dropdown component with consistent styling
 * Replaces native <select> elements with a styled dropdown
 *
 * @param {string} value - Currently selected value
 * @param {function} onChange - Callback when value changes
 * @param {Array} options - Array of { value, label } objects
 * @param {string} placeholder - Placeholder text when no value selected
 * @param {string} className - Additional CSS classes for the container
 * @param {string} width - Width class (e.g., 'w-44', 'w-full')
 * @param {boolean} disabled - Whether the dropdown is disabled
 * @param {boolean} error - Whether to show error styling
 */
const Dropdown = ({
  value,
  onChange,
  options = [],
  placeholder = 'Select...',
  className = '',
  width = 'w-44',
  disabled = false,
  error = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const selectedOption = options.find(opt => opt.value === value);
  const displayLabel = selectedOption?.label || placeholder;

  // Filter options based on search term
  const filteredOptions = searchTerm
    ? options.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setSearchTerm('');
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} className={`relative ${width} ${className}`}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full px-3 py-3 min-h-[48px]
          border rounded-lg
          text-sm text-left
          flex items-center justify-between gap-2
          transition-colors duration-150
          ${disabled
            ? 'bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed'
            : error
              ? 'bg-white border-red-300 text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 hover:border-red-400'
              : 'bg-white border-gray-300 text-slate-800 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 hover:border-gray-400'
          }
        `}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-disabled={disabled}
      >
        <span className="truncate">{displayLabel}</span>
        <ChevronDown
          className={`h-4 w-4 text-gray-500 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && !disabled && (
        <div
          className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg"
          role="listbox"
        >
          {/* Search Input - Only show if more than 5 options */}
          {options.length > 5 && (
            <div className="p-2 border-b border-gray-200">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Type to filter..."
                className="w-full px-3 py-3 min-h-[48px] text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
          )}

          {/* Options List - Filtered */}
          <div className="max-h-60 overflow-auto py-1">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const isSelected = option.value === value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleSelect(option.value)}
                    className={`
                      w-full px-3 py-3 min-h-[48px] text-sm text-left
                      flex items-center justify-between gap-2
                      transition-colors duration-100
                      ${isSelected
                        ? 'bg-cyan-50 text-cyan-700'
                        : 'text-slate-700 hover:bg-gray-50'
                      }
                    `}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <span className="truncate">{option.label}</span>
                    {isSelected && (
                      <Check className="h-4 w-4 text-cyan-600 flex-shrink-0" />
                    )}
                  </button>
                );
              })
            ) : (
              <div className="px-3 py-2 text-sm text-gray-500 italic">
                No options match "{searchTerm}"
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dropdown;
